::
<template>
  <div
    class="p-4 md:p-8 flex items-center justify-center relative overflow-hidden form-wrapper"
  >
    <div class="w-full max-w-7xl relative mx-auto">
      <h1 class="form-title">Enviar una solicitud</h1>
      <div
        class="form-container bg-white/[0.02] backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.2)] border border-white/10 hover:border-white/20 transition-all duration-500 relative"
      >
        <img
          src="@/assets/Fade.png"
          alt="Background effect"
          class="form-bg-image"
        />
        <img
          src="@/assets/Fade.png"
          alt="Background effect"
          class="form-bg-image-right"
        />
        <form
          @submit.prevent="handleSubmit"
          class="space-y-8 relative z-10"
          novalidate
        >
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div
              class="field-group lg:col-span-2 bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="email">
                Correo Electrónico
                <span class="required-marker text-[#3587F2]">*</span>
              </label>
              <div class="relative">
                <input
                  id="email"
                  type="email"
                  v-model="formData.email"
                  :aria-invalid="!!(formData.email && !isValidEmail)"
                  required
                  class="input-field pl-10"
                  :class="{
                    'border-green-500/50 focus:ring-green-500/50': isValidEmail,
                    'border-red-500/50 focus:ring-red-500/50':
                      !isValidEmail && formData.email,
                  }"
                  placeholder="tu@email.com"
                />
              </div>

              <!-- Missatge de validació de l'email -->
              <p
                v-if="formData.email && !isValidEmail"
                class="validation-msg error"
                role="alert"
              >
                Por favor, introduce un correo electrónico válido
              </p>
              <p
                v-else-if="formData.email && isValidEmail"
                class="validation-msg success"
              >
                Formato de correo válido
              </p>
            </div>

            <div
              class="field-group bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="requestType">
                Tipo de Solicitud
                <span class="required-marker text-[#3587F2]">*</span>
              </label>
              <select
                id="requestType"
                v-model="formData.requestType"
                required
                class="input-field"
              >
                <option value="" disabled selected>-</option>
                <option value="Bug">Error del sistema</option>
                <option value="Feature Request">
                  Solicitud de nueva funcionalidad
                </option>
                <option value="Product Question">
                  Pregunta sobre el producto
                </option>
                <option value="Technnical Support">Soporte técnico</option>
              </select>
            </div>

            <div
              class="field-group lg:col-span-3 bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="subject">
                Asunto
                <span class="required-marker text-[#3587F2]">*</span>
              </label>
              <input
                id="subject"
                type="text"
                v-model="formData.subject"
                required
                class="input-field"
                placeholder="Resume brevemente tu solicitud"
              />
            </div>

            <div
              class="field-group lg:col-span-3 bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="description">
                Descripción
                <span class="required-marker text-[#3587F2]">*</span>
              </label>

              <div class="flex items-center gap-2 mb-4">
                <button
                  type="button"
                  class="action-button zstw_open"
                  @click="recordVideo"
                  aria-label="Grabar video"
                >
                  <svg
                    class="action-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Grabar mi pantalla
                </button>
              </div>
              <textarea
                id="description"
                v-model="formData.description"
                required
                rows="6"
                :placeholder="descriptionPlaceholder"
                class="input-field textarea-field"
              ></textarea>
            </div>

            <div
              class="field-group lg:col-span-2 bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="impact">
                Impacto en el Negocio
              </label>
              <select id="impact" v-model="formData.impact" class="input-field">
                <option value="">-</option>
                <option
                  value="Bajo - Tienes una pregunta sobre Harmonix o cualquiera de sus funciones."
                >
                  Bajo - Tienes una pregunta sobre Harmonix o cualquiera de sus
                  funciones.
                </option>
                <option
                  value="Medio - Harmonix o tu configuración no está funcionando correctamente o funciona de forma intermitente."
                >
                  Medio - Harmonix o tu configuración no está funcionando
                  correctamente o funciona de forma intermitente.
                </option>
                <option
                  value="Alto - Harmonix o tu configuración causa una interrupción completa en tu flujo de trabajo."
                >
                  Alto - Harmonix o tu configuración causa una interrupción
                  completa en tu flujo de trabajo.
                </option>
              </select>
            </div>

            <div
              class="field-group bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label
                class="flex items-center justify-between field-label"
                for="impersonationToggle"
              >
                <span>Permitir Impersonar el Perfil</span>
                <button
                  id="impersonationToggle"
                  type="button"
                  @click="toggleImpersonation"
                  class="toggle-button"
                  :class="
                    formData.allowImpersonation ? 'bg-[#3587F2]' : 'bg-white/10'
                  "
                  :aria-pressed="formData.allowImpersonation"
                >
                  <span
                    class="toggle-thumb"
                    :class="
                      formData.allowImpersonation
                        ? 'translate-x-6'
                        : 'translate-x-1'
                    "
                  ></span>
                </button>
              </label>
              <p class="helper-text text-white/60 text-sm mt-2">
                Permitir a soporte acceder a tu cuenta para solucionar problemas
              </p>
            </div>

            <div
              class="field-group lg:col-span-2 bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="functionalArea">
                Área Funcional
              </label>
              <select
                id="functionalArea"
                v-model="formData.functionalArea"
                class="input-field"
              >
                <option value="">-</option>

                <optgroup label="Usuario y Configuración">
                  <option value="Problemas de inicio de sesión">
                    Problemas de inicio de sesión
                  </option>
                  <option value="Configuración de usuario">
                    Configuración de usuario
                  </option>
                </optgroup>

                <optgroup label="Comunicación">
                  <option value="Edición de correos electrónicos">
                    Edición de correos electrónicos
                  </option>
                  <option value="Sincronización de correo electrónico">
                    Sincronización de correo electrónico
                  </option>
                  <option value="Plantillas de correo electrónico">
                    Plantillas de correo electrónico
                  </option>
                  <option value="Seguimiento de correo electrónico">
                    Seguimiento de correo electrónico
                  </option>
                  <option value="Reuniones y calendario">
                    Reuniones y calendario
                  </option>
                  <option value="Notificaciones">Notificaciones</option>
                </optgroup>

                <optgroup label="Marcadores">
                  <option value="Incidencias en sistema de telefonía">
                    Incidencias en sistema de telefonía
                  </option>
                </optgroup>

                <optgroup label="Integraciones">
                  <option value="Actividad no registrada en el CRM">
                    Actividad no registrada en el CRM
                  </option>
                  <option value="Actividad no registrada en Harmonix">
                    Actividad no registrada en Harmonix
                  </option>
                </optgroup>

                <optgroup label="Funcionalidad de la App">
                  <option value="La aplicación se ha bloqueado">
                    La aplicación se ha bloqueado
                  </option>
                </optgroup>

                <optgroup label="Otros">
                  <option value="Otros">Otros</option>
                </optgroup>
              </select>
            </div>

            <div
              class="field-group lg:col-span-1 bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="usageContext">
                Contexto de Uso en Harmonix
              </label>
              <select
                id="usageContext"
                v-model="formData.usageContext"
                class="input-field"
              >
                <option value="">-</option>
                <option value="Salesforce">Salesforce</option>
                <option value="Microsoft Dynamics">Microsoft Dynamics</option>
                <option value="CRM Propio">CRM Propio</option>
                <option value="Otro CRM">Otro CRM</option>
              </select>
            </div>

            <div
              class="field-group lg:col-span-3 bg-white/[0.03] p-6 rounded-2xl border border-white/10 hover:border-[#3587F2]/30 transition-all duration-300"
            >
              <label class="field-label" for="fileUpload">
                Archivos Adjuntos
              </label>
              <div
                class="file-dropzone"
                @drop.prevent="onDrop"
                @dragover.prevent
                @dragenter.prevent="dragActive = true"
                @dragleave.prevent="dragActive = false"
                :class="{ 'border-[#3587F2]/50': dragActive }"
              >
                <input
                  id="fileUpload"
                  type="file"
                  ref="fileInput"
                  @change="onFileSelected"
                  multiple
                  class="hidden"
                />
                <div class="flex flex-col items-center space-y-2">
                  <svg
                    class="file-upload-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <div class="text-center">
                    <p class="file-drop-title">
                      Suelta archivos aquí o
                      <span
                        @click="triggerFileInput"
                        class="file-drop-link text-[#3587F2] cursor-pointer"
                      >
                        haz clic para subirlos
                      </span>
                    </p>
                    <p class="file-drop-subtitle">
                      Admite imágenes, videos y PDFs de hasta 10MB
                    </p>
                  </div>
                </div>
                <div
                  v-if="formData.files.length"
                  class="mt-4 border-t border-white/10 pt-4"
                >
                  <p class="text-white/70 text-sm mb-2">
                    Archivos seleccionados:
                  </p>
                  <ul class="space-y-1">
                    <li
                      v-for="(file, index) in formData.files"
                      :key="index"
                      class="text-white/60 text-sm flex justify-between"
                    >
                      <span>{{ file.name }}</span>
                      <button
                        @click="removeFile(index)"
                        class="text-red-400 hover:text-red-300"
                      >
                        Eliminar
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <p class="text-left text-white/70 text-sm mb-6">
            <span class="text-[#3587F2] m-2">*</span>Los campos marcados con un
            asterisco son obligatorios
          </p>

          <button
            type="submit"
            :disabled="!isFormValid || isSubmitting"
            :class="{
              'opacity-50 cursor-not-allowed': !isFormValid || isSubmitting,
              'animate-pulse': isSubmitting,
            }"
            class="submit-button"
          >
            <span v-if="!isSubmitting">Enviar Solicitud</span>
            <span v-else class="flex items-center justify-center">
              <svg
                class="spinner-icon w-5 h-5 mr-2 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                role="status"
                aria-label="Cargando"
              >
                <circle
                  class="spinner-bg opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="spinner-fg opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Procesando...
            </span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.form-wrapper {
  position: relative;
  z-index: 1;
}

.form-bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0.1;
  z-index: 0;
  pointer-events: none;
}

.form-bg-image-right {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: right;
  opacity: 0.1;
  z-index: 0;
  pointer-events: none;
}

.form-container {
  position: relative;
  z-index: 1;
  overflow: hidden;
}
</style>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useBirdieIntegration } from '../composables/useBirdieIntegration'
import { v4 as uuidv4 } from 'uuid'
import { submitToSalesforce } from '../utils/salesforceSubmit'
import { useRouter } from 'vue-router'

// Definim la interfície per al formulari
interface FormData {
  email: string
  requestType: string
  subject: string
  description: string
  impact: string
  allowImpersonation: boolean
  functionalArea: string
  usageContext: string
  files: File[]
}

const router = useRouter()

const descriptionPlaceholder =
  '❗ Por favor, describa su solicitud con el mayor detalle posible. Cuanta más información y ejemplos proporcione, más rápido podremos ayudarle. Los ejemplos son muy útiles para nosotros: puede adjuntar imágenes, capturas de pantalla o vídeos mediante el botón de arriba 👆. \n \n 🎥 Si graba un video, se generará un enlace al finalizar la grabación. **Por favor, copie ese enlace y pégalo aquí** para que podamos acceder al contenido. ¡Gracias por su colaboración!'

// Inicialitzem formData amb el tipus correcte
const formData = ref<FormData>({
  email: '',
  requestType: '',
  subject: '',
  description: '',
  impact: '',
  allowImpersonation: false,
  functionalArea: '',
  usageContext: '',
  files: [],
})

const dragActive = ref(false)
const isSubmitting = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

// Inicialitzem Birdie només una vegada
const { recordVideo } = useBirdieIntegration(formData, {
  contactName: 'Anonymous',
  contactEmail: '',
  contactId: uuidv4(),
})

// Validate email format
const isValidEmail = computed(() => {
  if (!formData.value.email) return true
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(formData.value.email)
})

// Validate overall form completion
const isFormValid = computed(() => {
  return (
    formData.value.email &&
    isValidEmail.value &&
    formData.value.requestType &&
    formData.value.subject &&
    formData.value.description
  )
})

// Handle form submission
const handleSubmit = async (event: Event) => {
  event.preventDefault()
  if (!isFormValid.value) return

  isSubmitting.value = true

  try {
    await submitToSalesforce(formData.value)
    router.push('/support/success')
  } catch (error) {
    console.error('Error submitting form:', error)
    router.push('/support/error')
  } finally {
    isSubmitting.value = false
  }
}

// Drag and drop file handling
const onDrop = (event: DragEvent) => {
  const droppedFiles = Array.from(event.dataTransfer?.files || [])
  formData.value.files.push(...droppedFiles)
  dragActive.value = false
}

// Open file input manually
const triggerFileInput = () => {
  fileInput.value?.click()
}

// Handle files selected manually
const onFileSelected = (event: Event) => {
  const selectedFiles = Array.from(
    (event.target as HTMLInputElement).files || []
  )
  formData.value.files.push(...selectedFiles)
}

// Remove a selected file
const removeFile = (index: number) => {
  formData.value.files.splice(index, 1)
}

// Toggle Allow Profile Impersonation
const toggleImpersonation = () => {
  formData.value.allowImpersonation = !formData.value.allowImpersonation
}
</script>

<style scoped lang="postcss">
/* -----------------------------
   Classes per a camps i elements del formulari
----------------------------- */
.field-label {
  @apply block text-white/90 mb-2;
  font-family: inherit;
  font-weight: 600;
  font-size: 16px;
  line-height: 140%;
}

.required-marker {
  @apply ml-1 text-[#3587F2];
}

.input-field {
  @apply w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:ring-2 focus:ring-[#3587F2]/50 focus:border-[#3587F2]/50 transition-all duration-300;
}

.textarea-field {
  @apply resize-none min-h-[120px];
}

.validation-msg {
  @apply mt-2 text-sm;
}

.validation-msg.error {
  @apply text-red-500/90;
}

.validation-msg.success {
  @apply text-green-500/90;
}

/* -----------------------------
   Botó d'acció addicional
----------------------------- */
.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#3686FF] rounded-xl hover:bg-[#3587F2]/30 transition-all duration-300;
}

.action-icon {
  @apply w-5 h-5 mr-2;
}

/* -----------------------------
   Toggle (Allow Profile Impersonation)
----------------------------- */
.toggle-button {
  @apply relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ml-8;
}

.toggle-thumb {
  @apply inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300;
}

/* -----------------------------
   File Upload (Dropzone)
----------------------------- */
.file-dropzone {
  @apply border-2 border-dashed border-white/20 rounded-xl p-10 text-center hover:border-[#3587F2]/50 transition-all duration-300 cursor-pointer bg-white/[0.02];
}

.file-upload-icon {
  @apply w-12 h-12 text-white/30;
}

.file-drop-title {
  @apply text-white/70 font-medium;
}

.file-drop-link {
  @apply underline hover:text-[#3587F2] transition-colors duration-200;
}

.file-drop-subtitle {
  @apply text-white/50 text-sm mt-1;
}

/* -----------------------------
   Botó d'enviament
----------------------------- */
.submit-button {
  @apply w-full bg-gradient-to-r from-[#3587F2] to-[#2563eb] text-white py-4 px-6 rounded-xl font-medium hover:opacity-90 transform hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-[#3587F2]/25 relative overflow-hidden;
}

/* -----------------------------
   Altres estils globals
----------------------------- */
html {
  scroll-behavior: smooth;
}
</style>
